import { Liquid } from 'liquidjs';
import { generateSystemPrompt, getAgentActions, getFirstMessage, getRequiredAgentVariables, isBuiltInAgentVariable } from '../../generate-system-prompt';
import { getBlockToolCallName } from '../../intake-form-builder/generate-prompt';
import { MAX_AGENT_NAME_LENGTH } from '../agent-tool-schemas';
import { checkAction, checkPostCallWorkflow } from './actions';
import { findInvalidFieldNames, fromAnalysisSchema } from './fields';
import { nodeTypeLabel, outcomeHandle } from './flow-operations';
import { intakeFormIssues } from '../intake-form/form-issues';
export const PROMPT_TOKEN_TARGET = 8_000;
export const PROMPT_TOKEN_MAX = 20_000;
const MAX_OUTCOMES = 5;
const MAX_OUTCOME_LENGTH = 150;
const MAX_ACTION_MESSAGE_WORDS = 8;
const LARGEST_PARTS_SHOWN = 5;
const VARIABLE_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)*$/;
const DEFAULT_ACTION_NAME = 'Execute_Action';
const DEFAULT_ACTION_DESCRIPTION = 'Execute an action';
// The real-time models use OpenAI tokenizers, at roughly four characters per token for English prose.
export const estimateTokens = (text) => Math.ceil(text.length / 4);
const liquid = new Liquid();
const flowIssues = (draft) => {
    const { nodes, edges } = draft.prompt.conversationFlow;
    const start = nodes.find((node) => node.type === 'startNode');
    if (!start) {
        return [
            {
                severity: 'error',
                where: 'Flow',
                message: 'There is no Conversation Start node.',
                fix: 'Add a Conversation Start node and connect it to the first step.'
            }
        ];
    }
    const reachable = new Set([start.id]);
    const queue = [start.id];
    while (queue.length > 0) {
        const current = queue.shift();
        edges
            .filter((edge) => edge.source === current && !reachable.has(edge.target))
            .forEach(({ target }) => {
            reachable.add(target);
            queue.push(target);
        });
    }
    const startData = start.data;
    const openings = [
        { direction: 'inbound', script: startData.script, mode: startData.firstMessageMode },
        ...(startData.outboundConfig
            ? [
                {
                    direction: 'outbound',
                    script: startData.outboundConfig.script,
                    mode: startData.outboundConfig.firstMessageMode
                }
            ]
            : [])
    ];
    const issues = [
        ...(edges.some((edge) => edge.target === start.id)
            ? [
                {
                    severity: 'error',
                    where: 'Conversation Start',
                    message: 'An outcome leads back into Conversation Start. The flow then has no entry point and compiles to nothing; point it at another node.'
                }
            ]
            : []),
        ...openings.flatMap(({ direction, script, mode }) => mode !== 'assistant-waits-for-user' && !script?.trim()
            ? [
                {
                    severity: 'warning',
                    where: 'Conversation Start',
                    message: `The agent speaks first on ${direction} conversations but has no ${direction} opening message.`,
                    fix: `Write the ${direction} opening message on Conversation Start, or have the agent wait for the other person to speak first.`
                }
            ]
            : []),
        ...(startData.outboundConfig && startData.outboundConfig.firstMessageMode !== 'assistant-waits-for-user'
            ? [
                {
                    severity: 'warning',
                    where: 'Conversation Start',
                    message: 'The agent speaks first on outbound calls, where people say "Hello?" before they listen. Outbound openings should wait for the person to speak.'
                }
            ]
            : []),
        ...(nodes.some((node) => node.type === 'endNode' && reachable.has(node.id))
            ? []
            : [
                {
                    severity: 'warning',
                    where: 'Flow',
                    message: 'No Conversation End node is reachable.',
                    fix: 'Connect the outcome that finishes the conversation to a Conversation End node.'
                }
            ])
    ];
    for (const node of nodes) {
        const where = node.data.label ? `"${node.data.label}"` : (nodeTypeLabel(node.type) ?? 'A step');
        const outcomes = node.data.outcomes ?? [];
        if (node.type === 'handoffNode') {
            issues.push({
                severity: 'warning',
                where,
                message: 'Handoff nodes are deprecated; use an action instead.'
            });
        }
        if (node.type === 'intakeNode' && !draft.prompt.intakeFormBuilder?.blocks?.length) {
            issues.push({
                severity: 'error',
                where,
                message: 'Its form has no blocks yet, so the agent collects nothing. Build it with edit_intake_form.'
            });
        }
        if (node.id !== start.id && !reachable.has(node.id)) {
            issues.push({
                severity: 'warning',
                where,
                message: "Nothing leads here from Conversation Start, so it's compiled as a separate flow.",
                fix: 'Connect an outcome of an earlier node to it, or delete it.'
            });
        }
        if ((node.type === 'agentTurnNode' || node.type === 'endNode') &&
            !node.data.instructions?.trim() &&
            !node.data.script?.trim()) {
            issues.push({
                severity: 'warning',
                where,
                message: 'It has neither instructions nor a script.',
                fix: 'Give it instructions for what the agent does here, or a script to say.'
            });
        }
        if (node.data.extraCallInstructions !== undefined &&
            (!node.data.extraCallInstructions.trim() || !node.data.extraChatInstructions?.trim())) {
            issues.push({
                severity: 'warning',
                where,
                message: 'Channel-specific instructions need both call and chat versions; one is empty.',
                fix: 'Fill in the empty version, or turn channel-specific instructions off for this node.'
            });
        }
        if ((node.type === 'agentTurnNode' || node.type === 'startNode') && outcomes.length === 0) {
            issues.push({
                severity: 'warning',
                where,
                message: "It has no outcomes, so the conversation can't move on.",
                fix: 'Add an outcome for each way this node can end, and connect each to the node that comes next.'
            });
        }
        if (outcomes.length > MAX_OUTCOMES) {
            issues.push({
                severity: 'info',
                where,
                message: `It has ${outcomes.length} outcomes; more than ${MAX_OUTCOMES} tend to overlap.`,
                fix: 'Merge the outcomes that lead to the same node.'
            });
        }
        outcomes.forEach((text, index) => {
            const outcome = `outcome ${index + 1}${text ? ` ("${text}")` : ''}`;
            if (!text.trim()) {
                issues.push({
                    severity: 'warning',
                    where,
                    message: `Outcome ${index + 1} has no text.`,
                    fix: 'Say when the agent should take this outcome, or delete it.'
                });
            }
            if (text.length > MAX_OUTCOME_LENGTH) {
                issues.push({
                    severity: 'info',
                    where,
                    message: `${outcome} is over ${MAX_OUTCOME_LENGTH} characters.`,
                    fix: "Cut it down to the condition, and move the rest into the node's instructions."
                });
            }
            if (!edges.some((edge) => edge.source === node.id && edge.sourceHandle === outcomeHandle(index))) {
                issues.push({
                    severity: 'warning',
                    where,
                    message: `${outcome} isn't connected, so it's left out of the prompt entirely.`,
                    fix: 'Connect it to the node that comes next, or delete it.'
                });
            }
        });
    }
    return issues;
};
const variableIssues = (draft, variables) => {
    const { prompt } = draft;
    const texts = [
        generateSystemPrompt(prompt, 'all'),
        getFirstMessage(prompt, 'all', 'inbound'),
        getFirstMessage(prompt, 'all', 'outbound')
    ];
    const unparsable = texts.some((text) => {
        try {
            liquid.parse(text);
            return false;
        }
        catch {
            return true;
        }
    });
    return [
        ...(unparsable
            ? [
                {
                    severity: 'error',
                    where: 'Variables',
                    message: "Some {{ }} or {% %} in the prompt is malformed, so variables in it won't be filled in.",
                    fix: 'Check that every {{ is closed by }} and every {% by %}.'
                }
            ]
            : []),
        ...variables
            .filter((name) => !VARIABLE_NAME_PATTERN.test(name))
            .map((name) => ({
            severity: 'warning',
            where: 'Variables',
            message: `"${name}" isn't a valid variable name; use letters, digits and underscores.`
        })),
        ...(prompt.voicemail?.enabled && prompt.voicemail.message?.includes('{{')
            ? [
                {
                    severity: 'warning',
                    where: 'Voicemail',
                    message: 'Variables in the voicemail message may not be filled in, so the agent could leave a broken message. Write it without them.'
                }
            ]
            : []),
        ...(prompt.voicemail?.enabled && !prompt.voicemail.message?.trim()
            ? [
                {
                    severity: 'warning',
                    where: 'Voicemail',
                    message: 'Voicemail is on but has no message.',
                    fix: 'Write the message the agent leaves, or turn voicemail off.'
                }
            ]
            : [])
    ];
};
// A claim intake agent's Validate step and form blocks bring their own actions, which aren't among the draft's.
const intakeActionNames = ({ conversationFlow, intakeFormBuilder }) => conversationFlow.nodes.some(({ type }) => type === 'intakeNode' || type === 'validateNode')
    ? [
        'validate_intake',
        ...(intakeFormBuilder?.blocks ?? []).map(({ title }) => getBlockToolCallName(title))
    ]
    : [];
const actionIssues = (draft) => {
    const callMentions = getAgentActions(draft.prompt, 'call');
    const chatMentions = getAgentActions(draft.prompt, 'chat');
    const names = Object.values(draft.actions).map(({ config }) => config.functionName);
    const builtInNames = intakeActionNames(draft.prompt);
    return [
        ...Object.entries(draft.actions).flatMap(([actionId, { config }]) => {
            const name = config.functionName;
            const where = `Action "${name}"`;
            const words = config.messagesStart?.trim().split(/\s+/).length ?? 0;
            return [
                ...(name === DEFAULT_ACTION_NAME
                    ? [
                        {
                            severity: 'error',
                            where,
                            message: 'It still has the default name; give it a descriptive snake_case name.'
                        }
                    ]
                    : []),
                ...(!config.functionDescription.trim() || config.functionDescription === DEFAULT_ACTION_DESCRIPTION
                    ? [
                        {
                            severity: 'error',
                            where,
                            message: 'It needs a description saying what it does and when to use it.'
                        }
                    ]
                    : []),
                ...checkAction(draft, actionId),
                ...(callMentions.includes(name) || chatMentions.includes(name)
                    ? []
                    : [
                        {
                            severity: 'warning',
                            where,
                            message: `Nothing in the flow mentions \`${name}\`, so the agent has no guidance on when to call it.`,
                            fix: 'Name it in backticks in the node where the agent should use it, or remove the action.'
                        }
                    ]),
                ...(callMentions.includes(name) && !chatMentions.includes(name)
                    ? [
                        {
                            severity: 'info',
                            where,
                            message: "It's only mentioned in call-specific text, so chat can't use it.",
                            fix: 'Mention it in the instructions chat reads too, if chat should use it.'
                        }
                    ]
                    : []),
                ...(words > MAX_ACTION_MESSAGE_WORDS
                    ? [
                        {
                            severity: 'info',
                            where,
                            message: 'Its action message is long; keep it to a few words.'
                        }
                    ]
                    : [])
            ];
        }),
        ...[...new Set([...callMentions, ...chatMentions])]
            .filter((mention) => !names.includes(mention) && !builtInNames.includes(mention))
            .map((mention) => ({
            severity: 'warning',
            where: 'Flow',
            message: `\`${mention}\` is in backticks, which marks an action, but there's no action by that name.`,
            fix: 'Add an action with that name, or take the backticks off.'
        })),
        ...checkPostCallWorkflow(draft)
    ];
};
const analysisIssues = (draft) => findInvalidFieldNames(fromAnalysisSchema(draft.prompt.analysisPlan?.schema)).map((name) => ({
    severity: 'error',
    where: 'Call analysis',
    message: `"${name}" isn't a valid field name; use letters, digits, underscores and hyphens.`
}));
const largestParts = (draft) => {
    const { prompt } = draft;
    const parts = [
        { part: 'Persona', text: prompt.role },
        { part: 'Objective', text: prompt.objective },
        {
            part: 'Advanced rules',
            text: [prompt.advancedPrompt, prompt.advancedPromptCallOnly, prompt.advancedPromptChatOnly].join('\n')
        },
        ...prompt.conversationFlow.nodes.map(({ data }) => ({
            part: `Node "${data.label}"`,
            text: [
                data.instructions,
                data.script,
                data.notes,
                data.extraCallInstructions,
                data.extraChatInstructions,
                ...(data.outcomes ?? [])
            ]
                .filter(Boolean)
                .join('\n')
        }))
    ];
    return parts
        .map(({ part, text }) => ({ part, tokens: estimateTokens(text ?? '') }))
        .sort((a, b) => b.tokens - a.tokens)
        .slice(0, LARGEST_PARTS_SHOWN);
};
const budgetIssues = (tokens) => {
    if (tokens > PROMPT_TOKEN_MAX) {
        return [
            {
                severity: 'warning',
                where: 'Prompt size',
                message: `The prompt is about ${tokens} tokens, over the ${PROMPT_TOKEN_MAX} limit.`,
                fix: 'Shorten its largest parts.'
            }
        ];
    }
    if (tokens > PROMPT_TOKEN_TARGET) {
        return [
            {
                severity: 'info',
                where: 'Prompt size',
                message: `The prompt is about ${tokens} tokens, over the ${PROMPT_TOKEN_TARGET} target.`,
                fix: 'Trim its largest parts where you can.'
            }
        ];
    }
    return [];
};
/** Everything check_agent reports about a draft: prompt size, runtime variables and problems to fix. */
export const checkAgentDraft = (draft, { name }) => {
    const promptTokens = {
        call: estimateTokens(generateSystemPrompt(draft.prompt, 'call')),
        chat: estimateTokens(generateSystemPrompt(draft.prompt, 'chat')),
        target: PROMPT_TOKEN_TARGET,
        max: PROMPT_TOKEN_MAX
    };
    const variables = getRequiredAgentVariables(draft.prompt);
    const issues = [
        ...(name.length > MAX_AGENT_NAME_LENGTH
            ? [
                {
                    severity: 'error',
                    where: 'Name',
                    message: `Agent names can be at most ${MAX_AGENT_NAME_LENGTH} characters.`,
                    fix: 'Shorten the name.'
                }
            ]
            : []),
        ...(draft.prompt.role.trim()
            ? []
            : [
                {
                    severity: 'warning',
                    where: 'Persona',
                    message: 'The persona is empty.',
                    fix: 'Describe who the agent is and how it should sound.'
                }
            ]),
        ...(draft.prompt.objective.trim()
            ? []
            : [
                {
                    severity: 'warning',
                    where: 'Objective',
                    message: 'The objective is empty, and it leads the prompt.',
                    fix: 'Say what the agent is there to do, in a sentence or two.'
                }
            ]),
        ...flowIssues(draft),
        ...intakeFormIssues(draft.prompt.intakeFormBuilder),
        ...variableIssues(draft, variables),
        ...actionIssues(draft),
        ...analysisIssues(draft),
        ...budgetIssues(Math.max(promptTokens.call, promptTokens.chat))
    ];
    return {
        promptTokens,
        largestParts: largestParts(draft),
        knowledgeBaseAttached: Boolean(draft.knowledgeBase),
        variables,
        variablesToPassIn: variables.filter((variable) => !isBuiltInAgentVariable(variable)),
        issues
    };
};
//# sourceMappingURL=check-agent.js.map